import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { BootstrapConfirmDialogData, CommonButtonRowWidget, UtilityService } from '@eforall/common';
import { DialogService } from '@eforall/dashboard';
import { AccApplicationStatusId, Trimester } from '@interfaces';
import { AppService, FuncService, TrimesterService } from '../../../../../services';
import { CountdownTimerWidget } from '../../../../../ui/widget';
import { AdmissionsAreaService } from '../../../admissions-area.service';
import { ApplicationStatusCountsWidget } from '../../../application-status-counts/application-status-counts.widget';


@Component({
	selector: 'admissions-manager-tab-1-part',
	standalone: true,
	imports: [ApplicationStatusCountsWidget, CountdownTimerWidget, CommonButtonRowWidget],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './admissions-manager-tab-1.part.html',
})
export class AdmissionsManagerTab1Part {

	private trimesterService = inject(TrimesterService);
	private func = inject(FuncService);
	private dialog = inject(DialogService);
	private areaService = inject(AdmissionsAreaService);
	private utils = inject(UtilityService);
	private app = inject(AppService);

	public canCloseAdmissions = computed(() => {
		return this.app.data().permissions.canCloseAdmissions;
	});

	public selectedTrimester = computed(() => this.trimesterService.selectedTrimester());

	// Track if deadline has passed (received from countdown timer widget)
	public isDeadlinePassed = signal(false);

	// Handle deadline status update from countdown timer
	public onDeadlineStatusChange(isPassed: boolean) {
		this.isDeadlinePassed.set(isPassed);
	}

	// Count of "Not Submitted" applications
	public notSubmittedCount = computed(() => {
		const applications = this.areaService.admissionsData()?.accApplications || [];
		const trimester = this.selectedTrimester();
		if (!trimester) return 0;
		return applications.filter((app) =>
			app.trimesterId === trimester.trimesterId &&
			app.accApplicationStatusId === AccApplicationStatusId.NotSubmitted
		).length;
	});

	// Formatted message for not submitted count
	public notSubmittedCountMessage = computed(() => {
		const count = this.notSubmittedCount();
		return `Currently ${this.utils.text.singularOrPluralWithCount(count, '"Not Submitted" application', '"Not Submitted" applications')} will be marked as Abandoned when you close all applications.`;
	});

	// Handle "Close all Applications" button click
	public async closeAllApplications() {
		const count = this.notSubmittedCount();
		const trimester = this.selectedTrimester();
		if (!trimester) return;

		if (count === 0) {
			await this.dialog.showMessage('No Applications to Close', 'There are no "Not Submitted" applications to close.', 'Okay', [], 400, 200);
			return;
		}

		// Show confirmation dialog
		const dialogData: BootstrapConfirmDialogData = {
			message: `Are you sure you want to Close all Applications? This will mark all ${this.utils.text.singularOrPluralWithCount(count, 'Not Submitted application', 'Not Submitted applications')} as Abandoned.`,
			yesButtonText: 'Yes, Close All',
			noButtonText: 'Cancel'
		};
		const yes = await this.dialog.confirm(dialogData.message, dialogData.yesButtonText, dialogData.noButtonText, [], 400, 300);

		if (yes) {
			try {
				const response = await this.func.accApplication.closeAllNotSubmittedApplications({
					trimesterId: trimester.trimesterId
				});

				// Reload admissions data to reflect the changes
				await this.areaService.reloadData();

				await this.dialog.showMessage('Applications Closed', `Successfully closed ${this.utils.text.singularOrPluralWithCount(response.updatedCount, 'application', 'applications')}.`, 'Okay', [], 400, 200);
			} catch (error) {
				await this.dialog.showMessage('Error', `Error closing applications: ${error}`);
			}
		}
	}

	public async getAccDetailsForTeamAnnouncement(trimester: Trimester) {
		const { trimesterId, isAdmissionsClosed, isTeamAnnouncementEmailSent } = trimester;

		if (isTeamAnnouncementEmailSent) {
			await this.dialog.showMessage('Team Announcement Already Sent', 'The Team Announcement email has already been sent for this trimester.', 'Okay', [], 400, 200);
			return;
		}

		const accDetails = await this.func.admissions.teamAnnouncement.getAccDetailsForTeamAnnouncement({ trimesterId });

		const bullets: string[] = [];

		accDetails.forEach((acc, index) => {
			const issues = [];

			if (!acc.kickOffEventUTC) issues.push('Kick-Off Event');
			if (!acc.classMeetGoogleCode) issues.push('Class Meet Google Code');
			if (!acc.facilitator.scheduleLink) issues.push('Facilitator Schedule Link');
			if (acc.hasMentors) {
				if (!acc.meetTheTeamEventUTC) issues.push('Meet the Team Event');
				if (!acc.volunteerContactStaff) issues.push('Volunteer Contact Staff');
			}

			if (issues.length > 0) {
				bullets.push(`${acc.accName} (${acc.facilitator.name}) : ${issues.join(', ')}`);
			}
		});


		if (bullets.length > 0) {

			await this.dialog.showMessage('Missing Details for Team Announcement', 'The following ACCs are missing details required for the Team Announcement email. Please update these details before sending the email.', 'Okay', bullets, 600, 500);

		} else if (!isAdmissionsClosed) {
			await this.dialog.showMessage('Cannot Send Team Announcement Yet', 'Team Announcement email can be sent only after Team Matching.', 'Okay', [], 400, 200);

		} else {
			const yes = await this.dialog.confirm('All ACCs have the necessary details for the Team Announcement email. Do you want to proceed?', 'Yes', 'No', [], 400, 200);

			if (yes) {
				await this.func.admissions.teamAnnouncement.sendTeamAnnouncementEmail({ trimesterId });
			}

		}
	}
}
